import { AttributeOption, SpecialOffer, SpecialOfferProduct } from "@/types/product.types";

/**
 * Resolves the final display price for a product + attribute option combination.
 *
 * Mirrors the backend `getFinalPrice` base-price logic:
 *  - The backend resolves `ProductAttributeOption.price` for this product+option
 *    and merges it into `option.price` before sending to the client.
 *  - If `option.price` is set → use it directly (per-product fixed price override).
 *  - Otherwise → apply `option.value` as a multiplier on `basePrice`.
 *  - If no option selected → return `basePrice` unchanged.
 *
 * @param basePrice - The product's effective price (use salePrice when discounted, else price)
 * @param option    - The selected AttributeOption (may be undefined/null)
 */
export function getOptionPrice(
    basePrice: number,
    option?: AttributeOption | null
): number {
    if (!option) return basePrice;

    // Per-product price override takes priority (resolved by backend from ProductAttributeOption)
    if (option.price !== null && option.price !== undefined && option.price !== "") {
        return Math.round(Number(option.price));
    }

    // Fall back to multiplier (value = null / undefined / 0 → treat as 1×)
    const multiplier = option.value ? Number(option.value) : 1;
    return Math.round(basePrice * multiplier);
}

/**
 * Computes the final sale price for a product inside a special offer.
 *
 * Priority rules:
 *  1. If the product is NOT excluded (`isExcluded: false`) → apply the global promotion discount.
 *  2. If the product IS excluded (`isExcluded: true`) and has an `overrideType` + `overrideValue`
 *     → apply ONLY its own individual override discount.
 *  3. If the product IS excluded but has NO override → it participates in the offer without any
 *     discount (returns the original price, no badge shown).
 */
export function computeOfferPrice(
    item: SpecialOfferProduct,
    offer: Pick<SpecialOffer, "discountType" | "discountValue">
): { salePrice: number; originalPrice: number; hasDiscount: boolean } {
    const product = item.product;
    if (!product) return { salePrice: 0, originalPrice: 0, hasDiscount: false };

    const originalPrice = Number(product.price || 0);
    let salePrice = originalPrice;
    let hasDiscount = false;

    if (!item.isExcluded) {
        // ── GLOBAL PROMOTION ────────────────────────────────────────────────
        const discountValue = Number(offer.discountValue || 0);
        if (offer.discountType === "PERCENTAGE" && discountValue > 0) {
            salePrice = originalPrice - (originalPrice * discountValue) / 100;
            hasDiscount = true;
        } else if (offer.discountType === "FIXED" && discountValue > 0) {
            salePrice = Math.max(0, originalPrice - discountValue);
            hasDiscount = true;
        }
    } else if (item.overrideType && item.overrideValue) {
        // ── INDIVIDUAL OVERRIDE ─────────────────────────────────────────────
        const overrideValue = Number(item.overrideValue);
        if (item.overrideType === "PERCENTAGE" && overrideValue > 0) {
            salePrice = originalPrice - (originalPrice * overrideValue) / 100;
            hasDiscount = true;
        } else if (item.overrideType === "FIXED" && overrideValue > 0) {
            salePrice = Math.max(0, originalPrice - overrideValue);
            hasDiscount = true;
        }
    }
    // else: excluded, no override → original price, no discount badge

    return { salePrice: Math.round(salePrice * 100) / 100, originalPrice, hasDiscount };
}
